import { useState, MouseEvent, useCallback } from 'react';
import type { Dispatch } from 'redux';
import { connect, useDispatch } from 'react-redux';

import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { IDropdownItem } from 'UIPackage/MainModule/interfaces/IDropdownItem';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { ExportFormatsEnum } from 'APIPackage/ImportExportModule/enums/ExportFormatsEnum';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';
import { erknmAnalyticsExportKnmList } from 'AnalyticsPackage/ErknmAnalyticsModule/actions/erknmAnalyticsActions';
import { IErknmAnalyticsFilter } from 'AnalyticsPackage/ErknmAnalyticsModule/interfaces/IErknmAnalyticsFilter';
import { getFilterFromParam } from 'AnalyticsPackage/KnmListModule/common/getFilterFromParam';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { erknmAnalyticsProsecutorOrganizationExportKnmList } from 'AnalyticsPackage/ErknmAnalyticsProsecutorOrganizationModule/actions/erknmAnalyticsProsecutorOrganizationActions';

import styles from './AnalyticsPanel.module.scss';
import { AnalyticsFiltersModal } from './AnalyticsFiltersModal';
import { IAnalyticsFilter } from '../interfaces/IAnalyticsFilter';
import { analyticsGetData, analyticsSetFilter, analyticsExport, analyticsKnmExport } from '../actions/analyticsActions';

interface IAnalyticsPanelProps {
  isErknm: boolean;
  filter: IAnalyticsFilter;
  isKnmList?: boolean;
  exportAnalytics: (format: ExportFormatsEnum) => void;
  exportAnalyticsKnm: (format: ExportFormatsEnum) => void;
  setFilter: (filter: IAnalyticsFilter) => void;
  fetchAnalytics: () => void;
  isOrganizationAnalytics?: boolean;
}

const AnalyticsPanelWithoutStore = ({
  isErknm,
  filter,
  isKnmList,
  fetchAnalytics,
  exportAnalytics,
  exportAnalyticsKnm,
  setFilter,
  isOrganizationAnalytics,
}: IAnalyticsPanelProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isFiltersModalVisible, setIsFiltersModalVisible] = useState(false);

  const handleFilterButtonClick = useCallback(() => setIsFiltersModalVisible(true), []);
  const closeFiltersModal = useCallback(() => setIsFiltersModalVisible(false), []);

  const handleExportClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const { format } = event.currentTarget.dataset;

      if (isErknm) {
        const urlParams = new URLSearchParams(window.location.search);
        const c = urlParams.get('c');
        const r = urlParams.get('r');
        const f = urlParams.get('f');
        const filterParams = getFilterFromParam<IErknmAnalyticsFilter>({});

        if (c && r && filterParams) {
          if (isOrganizationAnalytics) {
            dispatch(
              erknmAnalyticsProsecutorOrganizationExportKnmList(r, c, f, filterParams, format as ExportFormatsEnum),
            );
          } else {
            dispatch(erknmAnalyticsExportKnmList(r, c, f, filterParams, format as ExportFormatsEnum));
          }
        }
      } else if (isKnmList) {
        exportAnalyticsKnm(format as ExportFormatsEnum);
      } else {
        exportAnalytics(format as ExportFormatsEnum);
      }
    },
    [dispatch, exportAnalytics, exportAnalyticsKnm, isErknm, isKnmList],
  );

  const dropdownItems: IDropdownItem[] = [
    {
      id: 'export-ods-analytics',
      content: (
        <DropdownItem format={ExportFormatsEnum.ODS} onClick={handleExportClick}>
          Экспорт списка в ODS
        </DropdownItem>
      ),
    },
    {
      id: 'export-xlsx-analytics',
      content: (
        <DropdownItem format={ExportFormatsEnum.XLSX} onClick={handleExportClick}>
          Экспорт списка в XLSX
        </DropdownItem>
      ),
    },
  ];

  return (
    <div className="shared-panel-container">
      <div className={styles.FilterActionBlock}>
        {!isErknm && (
          <Button className={styles.FilterButton} text="Фильтры" color="primary" onClick={handleFilterButtonClick} />
        )}
        <Dropdown items={dropdownItems} />
      </div>
      <AnalyticsFiltersModal
        visible={isFiltersModalVisible}
        onClose={closeFiltersModal}
        filter={filter}
        setFilter={setFilter}
        onApply={fetchAnalytics}
      />
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  filter: state.analytics.filter,
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
});

const mapDispatchToProps = (dispatch: Dispatch<RootActions>) => ({
  exportAnalytics: (format: ExportFormatsEnum) => dispatch(analyticsExport(format)),
  exportAnalyticsKnm: (format: ExportFormatsEnum) => dispatch(analyticsKnmExport(format)),
  setFilter: (filter: IAnalyticsFilter) => dispatch(analyticsSetFilter(filter)),
  fetchAnalytics: () => dispatch(analyticsGetData()),
});

export const AnalyticsPanel = connect(mapStateToProps, mapDispatchToProps)(AnalyticsPanelWithoutStore);
